/**
 * @platform/sdk public entry.
 *
 * Usage from a game's index.js:
 *
 *   import { bootGame } from '@platform/sdk';
 *   import GameScene from './GameScene.js';
 *   import manifest from '../manifest.json' assert { type: 'json' };
 *
 *   bootGame({ manifest, GameScene });
 *
 * Everything else is automatic: scaling, scene flow, audio unlock,
 * networking, lobby, settings, error reporting.
 */
import { Boot } from './core/Boot.js';
import { Preload } from './core/Preload.js';
import { MainMenu } from './core/MainMenu.js';
import { Lobby } from './core/Lobby.js';
import { GameContainer } from './core/GameContainer.js';
import { GameOver } from './core/GameOver.js';
import { About } from './core/About.js';
import { Settings } from './core/Settings.js';
import { Pause } from './core/Pause.js';

import { Network } from './network/Network.js';
import { Sync } from './network/Sync.js';
import { Latency } from './network/Latency.js';
import { Reconnect } from './network/Reconnect.js';
import { Random } from './seed/Random.js';
import { Replay } from './replay/Replay.js';
import { AudioManager } from './audio/AudioManager.js';
import { Input } from './input/Input.js';
import { GamepadSource } from './input/GamepadSource.js';
import { VirtualJoystick } from './input/VirtualJoystick.js';
import { TouchButton } from './input/TouchButton.js';
import { TouchDPad } from './input/TouchDPad.js';
import { ArcadeController } from './input/ArcadeController.js';
import { DeviceOrientationSource } from './input/DeviceOrientationSource.js';
import { DeviceMotionSource } from './input/DeviceMotionSource.js';
import { CameraPassthrough } from './ar/CameraPassthrough.js';
import { PermissionsGate } from './ar/PermissionsGate.js';
import { TrackingManager } from './tracking/TrackingManager.js';
import { PoseTracker } from './tracking/PoseTracker.js';
import { FaceTracker } from './tracking/FaceTracker.js';
import { HandTracker } from './tracking/HandTracker.js';
import { I18n } from './i18n/I18n.js';
import { Leaderboard } from './multiplayer/Leaderboard.js';
import { Screenshot } from './screenshot/Screenshot.js';
import { TestApi } from './screenshot/TestApi.js';
import { FpsMonitor } from './perf/FpsMonitor.js';
import { QualityScaler } from './perf/QualityScaler.js';
import { Visibility } from './platform/Visibility.js';
import { ErrorReporter } from './platform/ErrorReporter.js';
import { Telemetry } from './platform/Telemetry.js';
import { platformStorage } from './save/Storage.js';
import { defaultManifest, validateManifest } from './manifest/load.js';
import { Theme } from './ui/Theme.js';
import { Fonts } from './ui/Fonts.js';

import { SdkGameScene } from './SdkGameScene.js';

export { SdkGameScene };
export { Network, Sync, Latency, Reconnect };
export { AudioManager, I18n, Random, Replay };
export { Input, GamepadSource, VirtualJoystick, TouchButton, TouchDPad, ArcadeController };
export { DeviceOrientationSource, DeviceMotionSource };
export { CameraPassthrough, PermissionsGate };
export { TrackingManager, PoseTracker, FaceTracker, HandTracker };
export { Leaderboard };
export { Screenshot };
export { Theme, Fonts };
export { SceneCamera } from './ui/SceneCamera.js';
export { PageBackground } from './platform/PageBackground.js';
export { PromptModal } from './ui/PromptModal.js';
export { Button } from './ui/Button.js';
export { SceneBackground } from './ui/SceneBackground.js';
export { onResize, layoutSlot } from './core/ResponsiveScene.js';
export { platformStorage, Storage } from './save/Storage.js';

// ── Reusable game-side helpers (extracted from existing games) ──────
export { CountdownDisplay } from './ui/CountdownDisplay.js';
export { HudRowAnimator } from './ui/HudRowAnimator.js';
export { EncouragementBanner } from './ui/EncouragementBanner.js';
export { HudPill } from './ui/HudPill.js';
export { ComboSystem, DEFAULT_COMBO_TIERS } from './gameplay/ComboSystem.js';
export { ParticleEmitter } from './fx/ParticleEmitter.js';
export { CameraShakeHelper } from './fx/CameraShakeHelper.js';
export { TapRipple } from './fx/TapRipple.js';

/**
 * Boot a game on the platform.
 *
 * @param {object} opts
 * @param {object} opts.manifest      The parsed manifest.json (or path-loaded version).
 * @param {Function} opts.GameScene   A class that extends SdkGameScene.
 * @param {string|HTMLElement} [opts.parent='game']  DOM mount.
 * @param {string} [opts.baseUrl='']  Prefix for asset URLs.
 * @param {object} [opts.bindings]    Custom input binding overrides.
 * @param {Function} [opts.telemetry] Optional telemetry sink.
 * @param {Function} [opts.errorSink] Optional error reporter sink.
 * @param {object}   [opts.peerOptions] Forwarded to PeerJS.
 * @returns {Phaser.Game}
 */
export function bootGame(opts) {
  const manifest = defaultManifest(validateManifest(opts.manifest));
  const baseUrl = opts.baseUrl || '';

  // With Scale.RESIZE the game size matches the browser window live, so
  // the "design dimensions" are only used as the *initial* size Phaser
  // creates the canvas with. Read the window dimensions directly so a
  // phone held in portrait gets a portrait initial size and a desktop
  // gets whatever window it has — then the scale manager follows any
  // subsequent resize / orientation change automatically.
  const hasWindow = typeof window !== 'undefined';
  const width  = hasWindow ? window.innerWidth  : 1280;
  const height = hasWindow ? window.innerHeight : 720;

  // Apply persisted player name default once.
  if (!platformStorage.get('playerName')) {
    platformStorage.set('playerName', 'Player' + Math.floor(Math.random() * 1000));
  }

  const arEnabled = manifest.requirements && manifest.requirements.ar === 'passthrough';
  // Games that render their own content behind Phaser (e.g. a Three.js
  // canvas for `tiny-blocks`) can pass `transparent: true` to let the
  // Phaser canvas composite on top of whatever they draw below.
  const transparentCanvas = arEnabled || !!opts.transparent;
  const pixelArt = !!opts.pixelArt;

  const game = new Phaser.Game({
    type: Phaser.AUTO, // Canvas fallback for old devices.
    // Transparent canvas in AR mode so the camera shows through, or when
    // the game opts in via `opts.transparent` (e.g. for a custom
    // WebGL canvas underneath).
    backgroundColor: transparentCanvas ? 'rgba(0,0,0,0)' : Theme.colors.bg,
    transparent: transparentCanvas,
    parent: opts.parent || 'game',
    scale: {
      // RESIZE: canvas size follows the browser window dimensions live.
      // No letterboxing (unlike FIT), no cropping (unlike ENVELOP), no
      // stretching — scenes read `scale.width` / `scale.height` to
      // position UI at the current live dimensions. Orientation changes
      // and window resizes are handled via `scale.on('resize', …)`
      // which `SdkGameScene` (and the shell scenes) hook into to
      // re-layout. See `core/ResponsiveScene.js`.
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width,
      height
    },
    fps: { target: 60, min: 20, smoothStep: true },
    physics: { default: 'arcade', arcade: { gravity: { y: 0 } } },
    // Enable DOM element container so scenes can mount HTML inputs
    // (used by `PromptModal` for the lobby "Join by code" input).
    dom: { createContainer: true },
    // Conservative renderer settings for low-end devices. `pixelArt: true`
    // switches texture filtering to NEAREST so pixel-art assets stay crisp
    // instead of getting bilinearly blurred.
    render: {
      antialias: !pixelArt,
      pixelArt: pixelArt,
      powerPreference: 'low-power',
      mipmapFilter: pixelArt ? 'NEAREST' : 'LINEAR'
    },
    // Games can replace the default multiplayer Lobby (host / join
    // room code) with a custom scene — e.g. `tiny-blocks` uses a
    // single-player "pick a world" screen. The custom class MUST set
    // its scene key to 'Lobby' via `super('Lobby')` so the stock
    // MainMenu.Play button still routes to it. We splice it into the
    // scene array at boot time instead of adding it post-hoc to avoid
    // Phaser's "duplicate key" error when the pending-scene queue
    // processes the original Lobby on the next tick.
    scene: [Boot, Preload, MainMenu, About, Settings, (opts.LobbyScene || Lobby), GameContainer, GameOver, Pause]
  });

  // Build the SDK service graph. None of these depend on a Phaser scene
  // existing — Input uses window-level listeners, Audio uses the game's
  // sound manager which is already alive after `new Phaser.Game(...)`.
  const i18n = new I18n();
  const audio = new AudioManager({ game });
  const screenshot = new Screenshot({ game });
  const fpsMonitor = new FpsMonitor();
  const qualityScaler = new QualityScaler({ fpsMonitor, game });
  const network = new Network({
    peerPrefix: manifest.id,
    gameVersion: manifest.version,
    peerOptions: opts.peerOptions
  });
  const sync = new Sync({
    network,
    snapshotRateHz: manifest.network.snapshotRateHz,
    tickRateHz: manifest.network.tickRateHz
  });
  const latency = new Latency({ network, sync });
  const reconnect = new Reconnect({ network, sync });
  const replay = new Replay();
  const telemetry = new Telemetry(opts.telemetry);
  const errorReporter = new ErrorReporter({ sink: opts.errorSink, meta: { gameId: manifest.id } });
  errorReporter.install();
  const input = new Input({ bindings: opts.bindings });
  const gamepadSource = new GamepadSource({
    input,
    mapping: opts.gamepadMapping,
    deadzone: opts.gamepadDeadzone
  });
  gamepadSource.install();

  // ── AR + sensors + tracking ─────────────────────────────────────────
  // Always constructed (so the SDK handle is uniform), but only `install()`d
  // / `start()`ed when the manifest opts in. Sensor sources are also useful
  // for non-AR games (gyro racing, shake-to-jump), so they're installed
  // whenever `requirements.gyroscope === true`.
  const orientationSource = new DeviceOrientationSource({ input });
  const motionSource = new DeviceMotionSource({ input });
  const camera = new CameraPassthrough({
    facingMode: opts.cameraFacing || 'environment'
  });
  const tracking = new TrackingManager();

  if (manifest.requirements && manifest.requirements.gyroscope) {
    // Will be a no-op in Node tests.
    orientationSource.install();
    motionSource.install();
  }

  if (arEnabled) {
    const needs = ['camera'];
    if (manifest.requirements.gyroscope) needs.push('orientation', 'motion');
    if (manifest.requirements.microphone) needs.push('microphone');

    const gate = new PermissionsGate({
      needs,
      onGranted: async () => {
        await camera.start();
        if (manifest.requirements.gyroscope) {
          orientationSource.install();
          motionSource.install();
        }
        const trackingKinds = (manifest.requirements.tracking || []).slice();
        if (trackingKinds.length) {
          try {
            await tracking.enable(trackingKinds);
            await tracking.startAll(camera.getVideoElement());
          } catch (err) {
            errorReporter.report({
              kind: 'tracking-load-failed',
              message: (err && err.message) || String(err)
            });
          }
        }
      }
    });
    // Defer slightly so document.body exists.
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => gate.show(), { once: true });
      } else {
        gate.show();
      }
    }
  }

  // Push everything into the registry now so Boot.create() can read it.
  const reg = game.registry;
  reg.set('manifest', manifest);
  reg.set('baseUrl', baseUrl);
  reg.set('GameSceneClass', opts.GameScene);
  reg.set('audio', audio);
  reg.set('i18n', i18n);
  reg.set('input', input);
  reg.set('gamepadSource', gamepadSource);
  // Per-game visual theme: backgrounds + button textures keyed by Phaser
  // cache name. The shell scenes (MainMenu, Lobby, About, Settings,
  // GameOver) read this at create() time and skin themselves accordingly.
  // No theme = current flat-Graphics look.
  reg.set('theme', opts.theme || null);
  reg.set('orientationSource', orientationSource);
  reg.set('motionSource', motionSource);
  reg.set('camera', camera);
  reg.set('tracking', tracking);
  reg.set('screenshot', screenshot);
  reg.set('fpsMonitor', fpsMonitor);
  reg.set('qualityScaler', qualityScaler);
  reg.set('network', network);
  reg.set('sync', sync);
  reg.set('latency', latency);
  reg.set('reconnect', reconnect);
  reg.set('replay', replay);
  reg.set('telemetry', telemetry);
  reg.set('errorReporter', errorReporter);
  telemetry.emit('boot', { gameId: manifest.id, version: manifest.version });

  // Pause locally when the tab is hidden; resume on return.
  new Visibility({
    onHidden: () => { try { game.scene.pause('UserGame'); } catch (_) { /* ignore */ } },
    onVisible: () => { try { game.scene.resume('UserGame'); } catch (_) { /* ignore */ } }
  });

  // Install the screenshot test API on `window.__platformSdkTest` only
  // when ?test=1 is set. Production game URLs never expose this.
  if (TestApi.enabled()) {
    const testApi = new TestApi({ game, screenshot });
    testApi.install();
    reg.set('testApi', testApi);
  }

  return game;
}
